import { ReactNode } from "react";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { SSO_ERROR_CODE } from "common/constants";
import { UserLoginErrorCode } from "../../generated/graphql";
import { BANNER_TYPES } from "../Common/__styles__/Banner";
import Banner from "../Common/Banner";
import { ButtonLink } from "../Common/ButtonLink";

const getUserLoginErrorTranslations = (
  t: (key: string) => string,
): Partial<Record<UserLoginErrorCode, string>> => ({
  [UserLoginErrorCode.INCORRECT_USERNAME_OR_PASSWORD]: t(
    "sign-in-incorrect-username-or-password",
  ),
  [UserLoginErrorCode.MISSING_EMAIL_OR_PASSWORD]: t(
    "sign-in-invalid-username-or-password",
  ),
  [UserLoginErrorCode.UNEXPECTED_ERROR]: t("sign-in-unexpected-error"),
});

const getSsoErrorTranslations = (
  t: TFunction<"translation", undefined>,
): Record<SSO_ERROR_CODE, string> => ({
  [SSO_ERROR_CODE.INVALID_STATE]: t("sign-in-sso-invalid-state"),
  [SSO_ERROR_CODE.PROVIDER_NOT_FOUND]: t("sign-in-sso-provider-not-found"),
  [SSO_ERROR_CODE.PROVIDER_NOT_ENABLED]: t("sign-in-sso-provider-not-enabled"),
  [SSO_ERROR_CODE.USER_NOT_FOUND]: t("sign-in-sso-user-not-found"),
  [SSO_ERROR_CODE.USER_SUSPENDED]: t("sign-in-sso-user-suspended"),
  [SSO_ERROR_CODE.INVALID_TOKEN]: t("sign-in-sso-invalid-token"),
  [SSO_ERROR_CODE.INVALID_REQUEST]: t("sign-in-sso-invalid-request"),
  [SSO_ERROR_CODE.INITIALIZATION_FAILED]: t(
    "sign-in-sso-initialization-failed",
  ),
  [SSO_ERROR_CODE.SESSION_CREATION_FAILED]: t(
    "sign-in-sso-session-creation-failed",
  ),
  [SSO_ERROR_CODE.AUTHENTICATION_FAILED]: t(
    "sign-in-sso-authentication-failed",
  ),
  [SSO_ERROR_CODE.SSO_FAILED]: t("sign-in-sso-failed"),
});

export const ErrorMessage = ({
  error,
  onClickPasswordReset,
}: {
  error: { message: string; code?: UserLoginErrorCode | SSO_ERROR_CODE };
  onClickPasswordReset?: () => void;
}) => {
  const { t } = useTranslation();

  const ssoErrorTranslations = getSsoErrorTranslations(t);
  const userLoginErrorTranslations = getUserLoginErrorTranslations(t);

  if (error.code) {
    const ssoMessage = ssoErrorTranslations[error.code as SSO_ERROR_CODE];
    if (ssoMessage) {
      return <ErrorMessageBanner>{ssoMessage}</ErrorMessageBanner>;
    }

    const userLoginMessage =
      userLoginErrorTranslations[error.code as UserLoginErrorCode];
    if (userLoginMessage) {
      return <ErrorMessageBanner>{userLoginMessage}</ErrorMessageBanner>;
    }
  }

  switch (error.code) {
    case UserLoginErrorCode.PASSWORD_EXPIRED: {
      if (onClickPasswordReset) {
        return (
          <ErrorMessageBanner>
            Your password has expired. Please{" "}
            <ButtonLink
              onClick={onClickPasswordReset}
              styleVariant="primary"
              size="small"
              style={{ display: "inline-block" }}
              data-testid="password-reset-button"
            >
              {t("sign-in-password-expired-link")}
            </ButtonLink>{" "}
            or contact support@withforerunner.com for assistance.
          </ErrorMessageBanner>
        );
      }
      return (
        <ErrorMessageBanner>{t("sign-in-password-expired")}</ErrorMessageBanner>
      );
    }
    default: {
      return <ErrorMessageBanner>{error.message}</ErrorMessageBanner>;
    }
  }
};

export const ErrorMessageBanner = ({ children }: { children: ReactNode }) => {
  return <Banner type={BANNER_TYPES.CRITICAL}>{children}</Banner>;
};
